import { useQuery } from '@tanstack/react-query'
import { postNamedQuery } from '../../../../service'
import { createIncrementalBuilder } from './buildTreeIncremental'
import { executeWithConcurrency } from './utils/concurrencyLimit'

/**
 * Load the FULL hierarchical dataset with parallel chunk loading
 * Optimized for large datasets (100k+ rows)
 *
 * @param {Object} query - Query configuration
 * @param {Function} onProgress - Optional callback for progress updates
 * @param {number} concurrency - Number of parallel requests (default: 4)
 * @param {number} chunkSize - Rows per chunk (default: 10000)
 */
export function useHierarchalReportDataFullParallel (query, onProgress, concurrency = 4, chunkSize = 10000) {
  const queryKeyString = JSON.stringify(query || {})

  return useQuery({
    queryKey: ['ddp/hierarchal-report/full-parallel', queryKeyString],
    queryFn: async () => {
      console.log('Loading hierarchical dataset with parallel fetching...')

      if (onProgress) {
        onProgress({ stage: 'counting', rowsLoaded: 0, message: 'Counting rows...' })
      }

      // Step 1: Get row count
      const { data: countResult } = await postNamedQuery('coreData', 'execute-data-pipeline', {
        pipelineCode: query.pipeline,
        variables: {
          filters: {
            ...(query?.variables || {}),
            query_type: 'row_count'
          }
        }
      })

      const totalRows = countResult?.rowCount || countResult?.data?.rowCount || 0
      console.log(`Total rows to fetch: ${totalRows}`)

      if (totalRows === 0) {
        return { data: [] }
      }

      if (onProgress) {
        onProgress({
          stage: 'loading',
          rowsLoaded: 0,
          totalRows,
          message: `Loading ${totalRows.toLocaleString()} rows in parallel...`
        })
      }

      // Step 2: Calculate chunks
      const numChunks = Math.ceil(totalRows / chunkSize)
      console.log(`Fetching ${numChunks} chunks with concurrency: ${concurrency}`)

      // Step 3: Create incremental tree builder (memory efficient - no flat array)
      const treeBuilder = createIncrementalBuilder(query.hierarchy)
      let rowsLoaded = 0

      if (onProgress) {
        onProgress({
          stage: 'building',
          rowsLoaded: 0,
          totalRows,
          message: 'Building tree incrementally as data loads...'
        })
      }

      // Step 4: Create chunk tasks
      const chunkTasks = Array.from({ length: numChunks }, (_, chunkIndex) => {
        return async () => {
          const skip = chunkIndex * chunkSize
          const take = Math.min(chunkSize, totalRows - skip)

          const { data: chunkData } = await postNamedQuery('coreData', 'execute-data-pipeline', {
            pipelineCode: query.pipeline,
            variables: {
              filters: {
                ...(query?.variables || {}),
                query_type: 'full',
                detailTake: take,
                detailSkip: skip
              }
            }
          })

          const pageData = chunkData?.data || []

          // Add chunk to tree incrementally (no intermediate storage)
          treeBuilder.addChunk(pageData)

          rowsLoaded += pageData.length

          if (onProgress) {
            // const stats = treeBuilder.getStats()
            onProgress({
              stage: 'building',
              rowsLoaded,
              totalRows,
              message: `Built ${rowsLoaded.toLocaleString()} of ${totalRows.toLocaleString()} rows`
            })
          }

          console.log(`Chunk ${chunkIndex + 1}/${numChunks} complete (${pageData.length} rows)`)

          return pageData.length
        }
      })

      // Step 5: Execute with concurrency limit
      await executeWithConcurrency(chunkTasks, concurrency)

      console.log(`All chunks processed: ${rowsLoaded} rows`)

      if (onProgress) {
        onProgress({
          stage: 'finalizing',
          rowsLoaded,
          totalRows,
          message: 'Finalizing tree structure...'
        })
      }

      // Step 6: Finalize tree (just serialization, no rebuild)
      const result = treeBuilder.finalize()
      console.log('Finalized hierarchical tree:', result)

      if (onProgress) {
        onProgress({
          stage: 'complete',
          rowsLoaded,
          totalRows,
          message: 'Report ready!'
        })
      }

      return result
    },
    staleTime: 60 * 60 * 1000, // Cache for 60 minutes
    cacheTime: 60 * 60 * 1000, // Keep in cache for 60 minutes
    refetchOnWindowFocus: false // Don't refetch on window focus
  })
}
